import { FeatureTypes } from "entity-app/constants";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useCIQLazyQuery, useCIQQuery } from "hooks/ciq-gql-hooks";
import { GridLoadingIndicator } from "components/widgets";
import { ProjectContext } from "context/ProjectProvider";
import HeaderTitle from "./components/header-title-ui";
import { getFeatureDetail, hasIntersection } from "./utils";
import {
  ENTITIES_DROPDOWN_NAMES,
  ENTITIES_LINKING_INFO
} from "./queries/queries";
import {
  DropDownDBFeatureInstance,
  TEditingEntity,
  TEntityLink,
  TLinkSource
} from "./models";
import LeftSideLinkData from "./components/left-side-link-data";
import LinkingInstructions from "./components/instructions";
import ListingLinkingWindow from "./components/listing-components";
import useActivityListHook from "./hooks/activity-list-hook";
import { initialTranslateEntityData } from "./utils/translate-to-linking-object";

import "./index.scss";

type Props = {
  featureTypeId: FeatureTypes;
  leftTabEntityTypes: FeatureTypes[];
  state?: any;
};

function FeatureLinking(props: Props) {
  const { featureTypeId, leftTabEntityTypes, state } = props;
  const { gqlClientForProject, eventLogs } = useContext(ProjectContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedEntityIds, setSelectedEntityIds] = useState<Array<string>>(
    () => {
      return state?.selectedRows?.map((x: any) => x.id) || [];
    }
  );

  const [dependentIdsForEventLog, setDependentIdsForEventLog] = useState<
    string[]
  >([]);

  const [selectedEntityInfo, setSelectedEntityInfo] = useState<TEntityLink>();

  const [editingEntity, setEditingEntity] = useState<TEditingEntity | null>(
    null
  );

  const onUpdateEditingEntityList = ({
    updatedlist
  }: {
    updatedlist: TLinkSource[];
  }) => {
    setEditingEntity((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        list: updatedlist
      };
    });
  };

  const activityListHook = useActivityListHook();

  const { data: featureSubmittalList, refetch: refetchSubmittalList } =
    useCIQQuery<{
      feature_instance: Array<DropDownDBFeatureInstance>;
    }>(ENTITIES_DROPDOWN_NAMES, {
      variables: {
        feature_type_id: FeatureTypes.SUBMITTALS,
        excluded_instance_id: []
      },
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const { data: featureMaterialList, refetch: refetchMaterialList } =
    useCIQQuery<{
      feature_instance: Array<DropDownDBFeatureInstance>;
    }>(ENTITIES_DROPDOWN_NAMES, {
      variables: {
        feature_type_id: FeatureTypes.MATERIALS,
        excluded_instance_id:
          selectedEntityIds.length > 0 ? selectedEntityIds : []
      },
      client: gqlClientForProject,
      skip: !gqlClientForProject
    });

  const [getEntityLinkingInfoQuery] = useCIQLazyQuery(ENTITIES_LINKING_INFO);

  const featureDetail = useMemo(
    () => getFeatureDetail(featureTypeId),
    [featureTypeId]
  );

  const getEntityInfo = useCallback(async () => {
    const response = await getEntityLinkingInfoQuery({
      feature_instance_ids: selectedEntityIds
    });

    if (response.data && response.data?.data?.feature_instance?.length) {
      setSelectedEntityInfo(
        initialTranslateEntityData(response.data.data.feature_instance)
      );
    }
  }, [getEntityLinkingInfoQuery, selectedEntityIds]);

  useEffect(() => {
    if (!editingEntity)
      setTimeout(() => {
        refetchMaterialList();
        refetchSubmittalList();
      }, 1000);
  }, [editingEntity, refetchMaterialList, refetchSubmittalList]);

  useEffect(() => {
    if (selectedEntityInfo) {
      const dependentIds = [
        ...selectedEntityInfo.ids,
        ...selectedEntityInfo.links.map((x) => x.featureInstanceId)
      ];

      setDependentIdsForEventLog(dependentIds);
    } else {
      setDependentIdsForEventLog([]);
    }
  }, [selectedEntityInfo]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      if (
        eventLogs.some((log) => log.data_source === "schedule_import_details")
      ) {
        activityListHook.refetchScheduleVersions();
      }

      if (!editingEntity) {
        const newEventLogs = eventLogs.filter((x) =>
          hasIntersection(
            x.info?.feature_instance_ids || [],
            dependentIdsForEventLog
          )
        );
        if (newEventLogs.length) {
          getEntityInfo();
        }
      }
    }
    previousEventLogs.current = eventLogs;
  }, [
    eventLogs,
    getEntityInfo,
    editingEntity,
    dependentIdsForEventLog,
    activityListHook
  ]);

  const getCallEntityInfoWithLoading = useCallback(async () => {
    setLoading(true);
    await getEntityInfo();
    setLoading(false);
  }, [getEntityInfo]);

  const prevIds = useRef<string[]>([]);
  useEffect(() => {
    if (
      selectedEntityIds.length > 0 &&
      !hasIntersection(prevIds.current, selectedEntityIds)
    ) {
      getCallEntityInfoWithLoading();
    }
    prevIds.current = selectedEntityIds;
  }, [getCallEntityInfoWithLoading, selectedEntityIds]);

  return (
    <div className="h-full w-full flex flex-col feature-entity-linking-page">
      <HeaderTitle
        featureDetail={featureDetail}
        selectedEntityIds={selectedEntityIds}
        setSelectedEntityIds={setSelectedEntityIds}
        selectedEntityInfo={selectedEntityInfo}
        setSelectedEntityInfo={setSelectedEntityInfo}
        refetchEntityInfo={getCallEntityInfoWithLoading}
        disable={!!editingEntity}
      />
      <div className="flex flex-col h-full w-full">
        {loading && (
          <div
            data-testid="feature-linking-loading"
            className="flex flex-col h-full w-full items-center justify-center"
          >
            <GridLoadingIndicator />
          </div>
        )}
        {!loading && selectedEntityInfo && (
          <div className="grid linking-group w-full">
            <LeftSideLinkData
              selectedEntityInfo={selectedEntityInfo}
              leftTabEntityTypes={leftTabEntityTypes}
              editingEntity={editingEntity}
              setEditingEntity={setEditingEntity}
              refetchEntityInfo={getEntityInfo}
              featureTypeId={featureTypeId}
            />

            {editingEntity ? (
              <ListingLinkingWindow
                editingEntity={editingEntity}
                onUpdateEditingEntityList={onUpdateEditingEntityList}
                setEditingEntity={setEditingEntity}
                materials={featureMaterialList?.feature_instance || []}
                submittals={featureSubmittalList?.feature_instance || []}
                activityListHook={activityListHook}
                selectedEntityInfo={selectedEntityInfo}
              />
            ) : (
              <div className="linking-group--right">
                <div className="flex h-10 items-center p-2.5">
                  <div className="font-bold">Instructions</div>
                </div>
                <LinkingInstructions
                  featureTypeId={featureTypeId}
                  isBulk={selectedEntityIds.length > 1}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FeatureLinking;
